import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connectSearchBox } from 'react-instantsearch-dom'
import Turbolinks from 'turbolinks'
import { Form, Input, Button, Grid, Box } from '@deseretbook/react-ui'
import { useMediaQuery } from '@deseretbook/react-utils'
import { Analytics } from '../../../dbcom'

/**
 * Custom searchbox component for Algolia
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/search-box/react/
 *
 * @param {string} params.currentRefinement - The current search query
 * @param {boolean} params.isSearchStalled - Whether the search is stalled
 * @param {function} params.refine - Formats the current query for search
 *
 * @returns {function} React component
 */
const SearchBoxComponent = ({ currentRefinement, isSearchStalled, refine }) => {
  const [debouncedSetState, setDebouncedSetState] = useState(null)
  const [query, setQuery] = useState(currentRefinement)

  const isMobileOrSmaller = useMediaQuery({ maxWidth: 650 })

  /**
   * Performs a keyword search on the current query
   *
   * @param {object} event - form submit object
   */
  const handleSubmit = (event) => {
    event.preventDefault()

    Analytics.event(
      {
        category: 'Algolia Search Box',
        action: `User performed manual search for "${currentRefinement}"`,
      },
      { isInteractive: true },
    )

    Turbolinks.visit(`/products?query=${encodeURIComponent(currentRefinement)}`)
  }

  /**
   * Updates the current search term
   *
   * @param {object} event - Input event object
   */
  const handleInputChange = (event) => {
    setQuery(event.currentTarget.value)
  }

  useEffect(() => {
    clearTimeout(debouncedSetState)

    setDebouncedSetState(
      setTimeout(() => {
        refine(query)
      }, 300),
    )
  }, [query])

  return (
    <div>
      <Form role="search" id="algolia-searchbox" onSubmit={handleSubmit}>
        <Box justifyContent="center" padding={0.5}>
          <Grid
            columns={[Grid.fr(1), Grid.minContent]}
            gap={0.5}
            width={isMobileOrSmaller ? '95%' : '80%'}
          >
            <Grid.Item>
              <Input
                name="search"
                type="search"
                value={query}
                onChange={handleInputChange}
                placeholder="Search for a Product, SKU, eBook, ..."
                isLoading={isSearchStalled}
                isEditableWhileLoading
              />
            </Grid.Item>
            <Grid.Item>
              <Button
                form="algolia-searchbox"
                color="action"
                isLoading={isSearchStalled}
                type="submit"
                icon="search"
                aria-label="Search for current term"
              >
                {isMobileOrSmaller ? null : 'Search'}
              </Button>
            </Grid.Item>
          </Grid>
        </Box>
      </Form>
    </div>
  )
}

SearchBoxComponent.propTypes = {
  currentRefinement: PropTypes.string,
  isSearchStalled: PropTypes.bool,
  refine: PropTypes.func,
}

export const SearchBox = connectSearchBox(SearchBoxComponent)
